import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ProBadge } from '@/components/ProBadge'
import { X, CalendarDays, Sparkles, BookOpen, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ExamSittingsList } from '@/components/ExamSittingsList'
import { SittingDateGrid } from '@/components/SittingDateGrid'
import { MonthCalendar } from '@/components/MonthCalendar'
import { addDaysISO } from '@/lib/monthCalendar'
import { getSittingsForExam, formatSittingDate, sittingContains, LOCALIZED_EXAMS, type ExamSitting } from '@/data/examSittings'
import {
  QUICK_SET_PRESETS,
  applyPreset,
  daysBetween,
  todayISO,
  formatReadableDate,
  type StudyPlanConfig,
  type QuickSetPreset,
  type TargetStrengthLevel,
} from '@/lib/studyPlan'
import { emitPlanLocked } from '@/lib/planForming'
import { useSoundOnMount } from '@/hooks/useSoundEffects'
import { OverlayPortal } from '@/components/ui/OverlayPortal'

const HEADLINE_PRESETS = ['1d', '1w', '2w'] as const

// Compact chip labels for the ready-date buffer presets (the hero date shows the resolved day)
const HEADLINE_PRESET_LABELS: Record<typeof HEADLINE_PRESETS[number], string> = {
  '1d': '1 day',
  '1w': '1 week',
  '2w': '2 weeks',
}

type StepKey = 'version' | 'examDate' | 'examDay' | 'ready' | 'strategy'

/** A window short enough to lay out as a grid of days gets a page of its own for picking the day. */
function hasDayPicker(s: ExamSitting | null): s is ExamSitting & { endDate: string } {
  return !!s?.endDate && s.endDate !== s.startDate && daysBetween(s.startDate, s.endDate) <= 35
}

interface Props {
  config: StudyPlanConfig
  examDate: string | null
  examLabel: string
  examId?: string
  initialStep?: number
  isPro?: boolean
  onSave: (next: Partial<StudyPlanConfig>) => void
  onExamDateChange?: (date: string | null) => void
  onClose: () => void
}

export function StudyPlanConfigModal({ config, examDate, examLabel, examId, initialStep, isPro = true, onSave, onExamDateChange, onClose }: Props) {
  // Paper: the panel sliding in.
  useSoundOnMount('open')
  const today = todayISO()

  const variants = examId ? (LOCALIZED_EXAMS[examId] ?? null) : null
  const hasVariants = variants !== null && variants.length > 0

  // `initialStep` counts the steps every plan has (version when localized,
  // exam date, ready date, strategy) — the exam-day page comes and goes with
  // the sitting picked, so callers never address it.
  const [chosenStep, setStep] = useState<StepKey>(() => {
    const fixed: StepKey[] = hasVariants ? ['version', 'examDate', 'ready', 'strategy'] : ['examDate', 'ready', 'strategy']
    if (initialStep !== undefined) return fixed[initialStep - 1] ?? 'examDate'
    if (hasVariants && !config.examVariant) return 'version'
    return 'examDate'
  })
  const [localExamVariant, setLocalExamVariant] = useState<string>(config.examVariant ?? '')
  const [localExamDate, setLocalExamDate] = useState(examDate ?? '')
  const [readyDate, setReadyDate] = useState(() => {
    if (config.targetReadyDate) return config.targetReadyDate
    const base = examDate
    if (base) return applyPreset(base, '2w')
    return ''
  })
  const [strength, setStrength] = useState<TargetStrengthLevel>(config.targetStrengthLevel)
  const [activePreset, setActivePreset] = useState<QuickSetPreset | null>(() => {
    if (config.targetReadyDate && examDate) {
      for (const p of QUICK_SET_PRESETS) {
        if (applyPreset(examDate, p) === config.targetReadyDate) return p
      }
      return null
    }
    return '2w'
  })
  const readyDateInputRef = useRef<HTMLInputElement>(null)
  const examDateInputRef = useRef<HTMLInputElement>(null)
  const [paperDay, setPaperDay] = useState(false)

  const sittings = examId ? getSittingsForExam(examId) : []
  const hasSittings = sittings.length > 0
  const selectedSitting = sittings.find(s => sittingContains(s, localExamDate)) ?? null
  // SOA's paper day falls inside its CBT window; a reader who picked the paper row has no day to choose.
  const daySitting = !paperDay && hasDayPicker(selectedSitting) ? selectedSitting : null

  const steps: StepKey[] = [
    ...(hasVariants ? ['version' as const] : []),
    'examDate',
    ...(daySitting ? ['examDay' as const] : []),
    'ready',
    'strategy',
  ]
  // A step that left the list (a single-day sitting picked) falls back to the exam date.
  const step: StepKey = steps.includes(chosenStep) ? chosenStep : 'examDate'
  const stepIndex = steps.indexOf(step)

  function selectPreset(preset: QuickSetPreset) {
    const base = localExamDate || examDate
    if (!base) return
    setReadyDate(applyPreset(base, preset))
    setActivePreset(preset)
  }

  function handleReadyDateChange(value: string) {
    setReadyDate(value)
    setActivePreset(null)
  }

  function handleExamDateChange(value: string) {
    setLocalExamDate(value)
    if (activePreset && value) {
      setReadyDate(applyPreset(value, activePreset))
    } else if (value && (!readyDate || (localExamDate && readyDate === applyPreset(localExamDate, '2w')))) {
      setReadyDate(applyPreset(value, '2w'))
    }
  }

  // Picking the sitting already picked moves on, without resetting a day chosen inside it.
  function handleExamSittingSelect(value: string, picked: ExamSitting) {
    const again = paperDay === !picked.endDate && (value === localExamDate || sittingContains(picked, localExamDate))
    setPaperDay(!picked.endDate)
    if (again) goNext()
    else handleExamDateChange(value)
  }

  function handleExamDaySelect(value: string) {
    if (value === localExamDate) goNext()
    else handleExamDateChange(value)
  }

  function handleSave() {
    onSave({
      targetReadyDate: readyDate || null,
      targetStrengthLevel: strength,
      ...(hasVariants && localExamVariant ? { examVariant: localExamVariant } : {}),
    })
    if (onExamDateChange) {
      onExamDateChange(localExamDate || null)
    }
    // The Dashboard's readiness card picks this up and plays the schedule
    // forming on its strip, once the plan has regenerated from the config above.
    if (examId) emitPlanLocked(examId)
    onClose()
  }

  const examDaysOut = localExamDate ? daysBetween(today, localExamDate) : null
  const daysOut = readyDate ? daysBetween(today, readyDate) : null
  const readyDateValid = !readyDate || (daysOut !== null && daysOut > 0)
  const examDateValid = !localExamDate || (examDaysOut !== null && examDaysOut > 0)

  function isNextDisabled(): boolean {
    if (step === 'version') return !localExamVariant
    if (step === 'examDate' || step === 'examDay') return !localExamDate || !examDateValid
    if (step === 'ready') return !!readyDate && !readyDateValid
    return false
  }

  function goNext() {
    if (stepIndex < steps.length - 1 && !isNextDisabled()) setStep(steps[stepIndex + 1])
  }

  return (
    <OverlayPortal>
    <div
      // z-[70]: above the popup stack and the sidebar drawer, both of which
      // can be open behind the surface that opens this.
      className="fixed inset-0 z-[70] flex items-start justify-center bg-background/80 backdrop-blur-sm p-4 overflow-y-auto paper-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Study plan configuration"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md bg-card rounded-2xl shadow-2xl flex flex-col my-12">
        {/* Header — just the exam name */}
        <div className="flex items-start justify-between px-6 pt-5">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground/70 leading-none">Study plan</p>
            <h2 className="text-xl font-bold leading-tight truncate mt-1">{examLabel}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground/50 hover:text-foreground p-1 -mr-1 -mt-1 transition-colors ml-2 shrink-0"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress — minimal segmented bar */}
        <div className="flex items-center gap-1.5 px-6 pt-4">
          {steps.map((s, i) => {
            const isActive = stepIndex === i
            const isDone = stepIndex > i
            const isStrategyLocked = s === 'strategy' && !isPro
            return (
              <button
                key={s}
                type="button"
                disabled={isStrategyLocked}
                onClick={() => {
                  if (isStrategyLocked) return
                  if (isActive) goNext()
                  else setStep(s)
                }}
                className="group flex-1 py-2 focus:outline-none disabled:cursor-not-allowed"
                aria-label={`Step ${i + 1} of ${steps.length}`}
                aria-current={isActive ? 'step' : undefined}
              >
                <div
                  className={`h-1 rounded-full transition-all duration-300 ${
                    isActive
                      ? 'bg-primary'
                      : isDone
                      ? 'bg-primary/40'
                      : 'bg-muted group-hover:bg-muted-foreground/20'
                  }`}
                />
              </button>
            )
          })}
        </div>

        <div className="px-6 pb-2 pt-5 space-y-5">

          {/* Step: Version selection (localized exams only) */}
          {step === 'version' && (
            <div className="space-y-4">
              <p className="text-lg font-semibold">Which version are you taking?</p>
              <div className="space-y-2">
                {variants!.map(v => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setLocalExamVariant(v.id)}
                    className={`w-full text-left rounded-xl p-3.5 text-sm font-semibold transition-all duration-200 ${
                      localExamVariant === v.id
                        ? 'bg-primary/10'
                        : 'bg-muted/40 hover:bg-accent/50'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Exam Date step */}
          {step === 'examDate' && (
            <div className="space-y-4">
              <p className="text-lg font-semibold">When is your exam?</p>

              {/* With no sittings to pick from, the calendar below is the way to change the day. */}
              {!hasSittings && localExamDate && examDaysOut !== null && examDaysOut > 0 && (
                <p className="text-4xl font-bold tracking-tight">{formatReadableDate(localExamDate)}</p>
              )}

              {hasSittings && localExamDate && examDaysOut !== null && examDaysOut > 0 && (
                <button
                  type="button"
                  onClick={() => { examDateInputRef.current?.showPicker?.() ?? examDateInputRef.current?.click() }}
                  className="group flex items-center gap-2.5 text-left"
                  aria-label="Change exam date"
                >
                  <span className="text-4xl font-bold tracking-tight">{formatReadableDate(localExamDate)}</span>
                  <CalendarDays className="h-5 w-5 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
                  <input
                    ref={examDateInputRef}
                    type="date"
                    value={localExamDate}
                    min={selectedSitting?.startDate ?? today}
                    max={selectedSitting?.endDate ?? undefined}
                    onChange={e => handleExamDateChange(e.target.value)}
                    className="sr-only dark:[color-scheme:dark]"
                  />
                </button>
              )}

              {hasSittings ? (
                examId && (
                  <ExamSittingsList
                    examId={examId}
                    selectedDate={localExamDate}
                    onSelect={handleExamSittingSelect}
                  />
                )
              ) : (
                <MonthCalendar
                  value={localExamDate}
                  min={addDaysISO(today, 1)}
                  onSelect={handleExamDateChange}
                />
              )}

              {localExamDate && examDaysOut !== null && examDaysOut <= 0 && (
                <p className="text-xs text-destructive">Date must be in the future</p>
              )}
            </div>
          )}

          {/* Exam Day step — the exact day inside a CBT window */}
          {step === 'examDay' && daySitting && (
            <div className="space-y-4">
              <div>
                <p className="text-lg font-semibold">Which day is your exam?</p>
                <p className="mt-0.5 text-xs text-muted-foreground/70">
                  The {formatSittingDate(daySitting)} window. Pick the day you booked, or your best guess.
                </p>
              </div>

              {localExamDate && (
                <p className="text-4xl font-bold tracking-tight">{formatReadableDate(localExamDate)}</p>
              )}

              <SittingDateGrid
                startDate={daySitting.startDate}
                endDate={daySitting.endDate}
                selectedDate={localExamDate}
                onSelect={handleExamDaySelect}
              />

              {localExamDate && examDaysOut !== null && examDaysOut <= 0 && (
                <p className="text-xs text-destructive">Date must be in the future</p>
              )}
            </div>
          )}

          {/* Ready Date step */}
          {step === 'ready' && (
            <div className="space-y-4">
              <p className="text-lg font-semibold">When do you want to be exam-ready?</p>

              {/* Selected ready date — tap to pick a custom day */}
              {readyDate && (
                <button
                  type="button"
                  onClick={() => { readyDateInputRef.current?.showPicker?.() ?? readyDateInputRef.current?.click() }}
                  className="group flex items-center gap-2.5 text-left"
                  aria-label="Change ready date"
                >
                  <span className="text-4xl font-bold tracking-tight">{formatReadableDate(readyDate)}</span>
                  <CalendarDays className="h-5 w-5 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
                  <input
                    ref={readyDateInputRef}
                    type="date"
                    value={readyDate}
                    min={today}
                    max={localExamDate || examDate || undefined}
                    onChange={e => handleReadyDateChange(e.target.value)}
                    className="sr-only dark:[color-scheme:dark]"
                  />
                </button>
              )}

              {/* Buffer presets — the hero date above shows the resolved day */}
              {localExamDate && (
                <div className="grid grid-cols-3 gap-2">
                  {HEADLINE_PRESETS.map(p => {
                    const isActive = activePreset === p
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => selectPreset(p)}
                        className={`rounded-xl py-2.5 text-sm font-semibold transition-all duration-200 ${
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted/40 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {HEADLINE_PRESET_LABELS[p]}
                      </button>
                    )
                  })}
                </div>
              )}
              <p className="text-xs text-muted-foreground/70">A buffer before exam day to review and rest.</p>

              {readyDate && daysOut !== null && daysOut <= 0 && (
                <p className="text-xs text-destructive">Date must be in the future</p>
              )}

              {!isPro && (
                <div className="flex items-start gap-2 rounded-lg bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
                  <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary/60" />
                  <span>
                    Study strategy customization is a{' '}
                    <Link to="/upgrade" className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"><ProBadge /></Link>{' '}
                    feature.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Study Strategy step */}
          {step === 'strategy' && (
            <div className="space-y-4">
              <p className="text-lg font-semibold">How do you want to study?</p>

              <div className="space-y-2">
                {([
                  {
                    value: 'strong_all' as TargetStrengthLevel,
                    label: 'Master everything',
                    desc: 'Work through the syllabus in the order it teaches it.',
                    icon: BookOpen,
                  },
                  {
                    value: 'strong_key' as TargetStrengthLevel,
                    label: 'Heaviest topics first',
                    desc: 'Start with the topics that carry the most weight on the exam.',
                    icon: Sparkles,
                  },
                ] as const).map(opt => {
                  const Icon = opt.icon
                  const isActive = strength === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStrength(opt.value)}
                      className={`w-full text-left rounded-xl p-4 transition-all duration-200 ${
                        isActive
                          ? 'bg-primary/10'
                          : 'bg-muted/40 hover:bg-accent/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                            isActive
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold">{opt.label}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">{opt.desc}</p>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-3 flex gap-2 justify-between">
          <div>
            {stepIndex > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep(steps[stepIndex - 1])}>
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {stepIndex === 0 && (
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
            )}
            {stepIndex < steps.length - 1 && !(step === 'ready' && !isPro) ? (
              <Button
                size="sm"
                onClick={goNext}
                disabled={isNextDisabled()}
              >
                Next
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!!readyDate && !readyDateValid}
              >
                Lock in plan
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    </OverlayPortal>
  )
}
